import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, MessageCircle, ArrowRight, Calendar, Phone, Clock } from 'lucide-react';
import api from '@/lib/api';

/**
 * SavedContactLanding — `/dr/:slug/hi`
 *
 * The "what's next?" page a patient lands on after scanning the
 * doctor's Save-contact QR. Two parallel jobs:
 *
 *   1. Reinforce the save ("✓ Saved Dr. … to your contacts").
 *   2. Convert the save into an actual lead with ONE giant CTA that
 *      either fires WhatsApp (if the doctor opted-in to share their
 *      phone) or opens the in-app chat (privacy-default path).
 *
 * Why a dedicated page rather than a section on /dr/:slug?
 *   - It's the URL embedded inside the saved vCard contact — the
 *     patient can re-open it from their phone's Contacts app weeks
 *     later and still hit the conversion CTA.
 *   - Cleaner analytics: every visit here is an attributable
 *     post-scan lead intent.
 */
export default function SavedContactLanding() {
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Earliest available slot — surfaced under the Message CTA so a warm
  // QR-scanner can pick a time without sending a message first. Optional:
  // if the doctor hasn't set a schedule yet, this stays null and the card
  // simply isn't shown.
  const [nextSlot, setNextSlot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/doctor/${slug}`)
      .then(r => { if (!cancelled) { setDoctor(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    // Parallel fetch — non-blocking. Bail silently on error.
    api.get(`/doctor/${slug}/next-available`)
      .then(r => { if (!cancelled && r.data) setNextSlot(r.data); })
      .catch(() => {});
    // Fire-and-forget attribution event so we can see the conversion
    // funnel in /dashboard/leads (source=qr-saved-landing).
    api.post('/track/event', {
      doctor_slug: slug,
      event_type: 'page_view',
      source: 'qr-saved-landing',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }
  if (error || !doctor) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-slate-700 mb-3">We couldn't find this doctor's page.</p>
          <Link to="/" className="text-sky-600 hover:text-sky-700 text-sm font-medium">Go home →</Link>
        </div>
      </div>
    );
  }

  // Build the WhatsApp deep-link only when the doctor has explicitly
  // opted in to share their phone in the QR. For everyone else, we
  // route the patient back into the in-app chat flow — same lead
  // capture, doctor's number stays private.
  const phoneE164 = (doctor.phone || '').replace(/\D/g, '').slice(-10);
  const canWhatsApp = !!doctor.qr_share_phone && phoneE164.length === 10;
  const waText = encodeURIComponent(
    `Hi Dr. ${(doctor.name || '').replace(/^Dr\.?\s+/i, '')}, I just saved your contact and would like to book an appointment.`
  );
  const waUrl = canWhatsApp ? `https://wa.me/91${phoneE164}?text=${waText}` : '';
  // When the doctor's phone is private, route the patient into the
  // existing Connect page (/c/:slug) which handles identity check + in-app
  // messaging — the doctor's number stays private end to end.
  const inAppChatUrl = `/c/${slug}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/60 via-white to-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{`Saved · ${doctor.name} | DocSite`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-md mx-auto px-5 pt-12 pb-16">
        {/* Confirmation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Saved to your contacts
          </h1>
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-900">{doctor.name}</span> is now in your phonebook.
          </p>
        </div>

        {/* Doctor card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5 shadow-sm">
          <div className="flex items-start gap-4">
            {doctor.photo_url ? (
              <img src={doctor.photo_url} alt={doctor.name} className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 font-semibold text-lg">
                {(doctor.name || '').split(' ').slice(-1)[0]?.[0] || 'D'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 truncate" data-testid="saved-landing-doctor-name">{doctor.name}</p>
              {doctor.affiliations && (
                <p className="text-xs text-slate-700 font-medium truncate" data-testid="saved-landing-doctor-affiliations">{doctor.affiliations}</p>
              )}
              {doctor.qualifications && <p className="text-xs text-slate-500 truncate">{doctor.qualifications}</p>}
              {Array.isArray(doctor.specializations) && doctor.specializations.length > 0 && (
                <p className="text-xs text-slate-600 mt-1 truncate">
                  {doctor.specializations.slice(0, 2).map(s => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(' · ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* PRIMARY CTA — adaptive */}
        {canWhatsApp ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              api.post('/track/event', { doctor_slug: slug, event_type: 'whatsapp_click', source: 'qr-saved-landing' }).catch(() => {});
            }}
            className="block w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl px-5 py-4 text-center font-semibold shadow-lg shadow-emerald-500/20 transition-colors"
            data-testid="saved-landing-whatsapp-cta"
          >
            <span className="flex items-center justify-center gap-2">
              <MessageCircle size={18} />
              Message Dr. {(doctor.name || '').split(' ').slice(-1)[0]} on WhatsApp
              <ArrowRight size={16} />
            </span>
            <span className="block text-[11px] font-normal text-emerald-50 mt-1">Opens with a ready-to-send message</span>
          </a>
        ) : (
          <Link
            to={inAppChatUrl}
            onClick={() => {
              api.post('/track/event', { doctor_slug: slug, event_type: 'chat_click', source: 'qr-saved-landing' }).catch(() => {});
            }}
            className="block w-full bg-sky-600 hover:bg-sky-700 text-white rounded-2xl px-5 py-4 text-center font-semibold shadow-lg shadow-sky-600/20 transition-colors"
            data-testid="saved-landing-chat-cta"
          >
            <span className="flex items-center justify-center gap-2">
              <MessageCircle size={18} />
              Message Dr. {(doctor.name || '').split(' ').slice(-1)[0]}
              <ArrowRight size={16} />
            </span>
            <span className="block text-[11px] font-normal text-sky-50 mt-1">Secure in-app chat · the doctor will know you enquired</span>
          </Link>
        )}

        {/* EARLIEST AVAILABLE — warm-lead nudge. Hidden if doctor has no
            schedule set up. Tapping opens the book flow with the slot
            pre-selected via the URL query (?date=...&time=...). */}
        {nextSlot && (
          <Link
            to={`/dr/${slug}?date=${nextSlot.date}&time=${encodeURIComponent(nextSlot.start_time)}&loc=${nextSlot.location_id}#book`}
            onClick={() => {
              api.post('/track/event', { doctor_slug: slug, event_type: 'booking_click', source: 'qr-saved-landing' }).catch(() => {});
            }}
            className="block mt-3 bg-white border border-emerald-200 rounded-2xl px-4 py-3.5 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
            data-testid="saved-landing-next-slot"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Clock size={16} className="text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-0.5">Earliest available</p>
                  <p className="text-sm font-semibold text-slate-900">
                    <span data-testid="next-slot-day">{nextSlot.day_label}</span>
                    <span className="text-slate-400 mx-1.5">·</span>
                    <span data-testid="next-slot-time">{nextSlot.start_time}</span>
                  </p>
                  {nextSlot.location_name && (
                    <p className="text-[11px] text-slate-500 truncate mt-0.5">{nextSlot.location_name}</p>
                  )}
                </div>
              </div>
              <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
            </div>
          </Link>
        )}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Link
            to={`/dr/${slug}`}
            className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl py-3.5 px-3 hover:bg-slate-50 transition-colors"
            data-testid="saved-landing-profile-cta"
          >
            <Phone size={16} className="text-slate-500 mb-1.5" />
            <span className="text-xs font-semibold text-slate-700">View profile</span>
          </Link>
          <Link
            to={`/dr/${slug}#book`}
            className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl py-3.5 px-3 hover:bg-slate-50 transition-colors"
            data-testid="saved-landing-book-cta"
          >
            <Calendar size={16} className="text-slate-500 mb-1.5" />
            <span className="text-xs font-semibold text-slate-700">Book appointment</span>
          </Link>
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-slate-400 mt-10">
          Powered by DocSite&trade;
        </p>
      </div>
    </div>
  );
}
